import React, { useEffect, useState, useRef } from 'react';
import {
    Button,
    Card,
    Table,
    Modal,
    Form,
    Input,
    InputNumber,
    Switch,
    Space,
    message,
    Popconfirm,
    Tag,
    DatePicker,
    Select,
    Descriptions,
    Tour,
    ConfigProvider,
    Row,
    Col,
    Alert,
    Badge,
    Tooltip,
    Statistic,
} from 'antd';
import type { TableColumnsType, TourProps } from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CalculatorOutlined,
    QuestionCircleOutlined,
    EyeOutlined,
    StarOutlined,
    StarFilled,
    PercentageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import viVN from 'antd/locale/vi_VN';

import AdminLayout from '../components/AdminLayout';
import { vatService } from '../services/vatService';
import { VATTax, VATTaxCreateInput, VATTaxUpdateInput, ProductCategory } from '../types/vat';

const { TextArea } = Input;
const { Option } = Select;

const VATManagement: React.FC = () => {
    const [vatTaxes, setVatTaxes] = useState<VATTax[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [calculatorModalVisible, setCalculatorModalVisible] = useState(false);
    const [editingVATTax, setEditingVATTax] = useState<VATTax | null>(null);
    const [viewingVATTax, setViewingVATTax] = useState<VATTax | null>(null);
    const [form] = Form.useForm();
    const [calculatorForm] = Form.useForm();
    const [isTourOpen, setIsTourOpen] = useState(false);

    // Loading states
    const [submitLoading, setSubmitLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
    const [toggleLoading, setToggleLoading] = useState<string | null>(null);
    const [defaultLoading, setDefaultLoading] = useState<string | null>(null);

    // Calculator states
    const [calculatorResult, setCalculatorResult] = useState<{
        originalAmount: number;
        vatRate: number;
        vatAmount: number;
        totalAmount: number;
    } | null>(null);

    // Filter states
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');

    // Tour refs
    const titleRef = useRef(null);
    const addButtonRef = useRef(null);
    const tableRef = useRef(null);
    const calculatorButtonRef = useRef(null);

    // Available product categories
    const productCategories = [
        { value: ProductCategory.COFFEE, label: 'Hạt cà phê' },
        { value: ProductCategory.BOTTLED_DRINK, label: 'Đồ uống đóng chai' },
        { value: ProductCategory.DISH, label: 'Đồ uống/Món ăn' },
    ];

    // Tour steps
    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý thuế VAT',
            description: 'Chào mừng bạn đến với trang Quản lý thuế VAT! Đây là nơi bạn có thể quản lý các mức thuế VAT khác nhau cho từng loại sản phẩm.',
            target: () => titleRef.current,
        },
        {
            title: 'Thêm thuế VAT',
            description: 'Nhấn vào nút này để tạo mức thuế VAT mới. Bạn có thể thiết lập tỷ lệ thuế, loại sản phẩm áp dụng và thời gian hiệu lực.',
            target: () => addButtonRef.current,
        },
        {
            title: 'Máy tính thuế VAT',
            description: 'Sử dụng máy tính này để tính toán nhanh thuế VAT cho các giao dịch.',
            target: () => calculatorButtonRef.current,
        },
        {
            title: 'Danh sách thuế VAT',
            description: 'Bảng này hiển thị tất cả các mức thuế VAT. Bạn có thể xem, chỉnh sửa, xóa hoặc đặt làm thuế mặc định.',
            target: () => tableRef.current,
        },
    ];

    useEffect(() => {
        fetchVATTaxes();
    }, []);

    const fetchVATTaxes = async () => {
        setLoading(true);
        try {
            const data = await vatService.getAllVATTaxes();
            setVatTaxes(data);
        } catch (error) {
            console.error('Error fetching VAT taxes:', error);
            message.error('Không thể tải danh sách thuế VAT');
        }
        setLoading(false);
    };

    const handleAdd = () => {
        setEditingVATTax(null);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            isDefault: false,
            applicableCategories: []
        });
        setModalVisible(true);
    };

    const handleEdit = (record: VATTax) => {
        setEditingVATTax(record);
        form.setFieldsValue({
            ...record,
            effectiveDate: record.effectiveDate ? dayjs(record.effectiveDate) : null,
            expiredDate: record.expiredDate ? dayjs(record.expiredDate) : null,
        });
        setModalVisible(true);
    };

    const handleView = (record: VATTax) => {
        setViewingVATTax(record);
        setDetailModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        setDeleteLoading(id);
        try {
            await vatService.deleteVATTax(id);
            message.success('Đã xóa thuế VAT thành công!');
            fetchVATTaxes();
        } catch (error) {
            console.error('Error deleting VAT tax:', error);
            message.error('Không thể xóa thuế VAT. Vui lòng thử lại!');
        } finally {
            setDeleteLoading(null);
        }
    };

    const handleToggleStatus = async (id: string, isActive: boolean) => {
        setToggleLoading(id);
        try {
            await vatService.toggleVATTaxStatus(id, isActive);
            message.success(`Đã ${isActive ? 'kích hoạt' : 'tạm dừng'} thuế VAT thành công!`);
            fetchVATTaxes();
        } catch (error) {
            console.error('Error toggling VAT tax status:', error);
            message.error('Không thể thay đổi trạng thái thuế VAT. Vui lòng thử lại!');
        } finally {
            setToggleLoading(null);
        }
    };

    const handleSetDefault = async (id: string) => {
        setDefaultLoading(id);
        try {
            await vatService.setDefaultVATTax(id);
            message.success('Đã đặt làm thuế VAT mặc định thành công!');
            fetchVATTaxes();
        } catch (error) {
            console.error('Error setting default VAT tax:', error);
            message.error('Không thể đặt thuế VAT mặc định. Vui lòng thử lại!');
        } finally {
            setDefaultLoading(null);
        }
    };

    const handleSubmit = async (values: any) => {
        setSubmitLoading(true);
        try {
            const formattedValues: VATTaxCreateInput | VATTaxUpdateInput = {
                ...values,
                effectiveDate: values.effectiveDate?.toDate(),
                expiredDate: values.expiredDate?.toDate(),
            };

            if (editingVATTax) {
                await vatService.updateVATTax({
                    ...formattedValues,
                    id: editingVATTax.id!,
                } as VATTaxUpdateInput);
                message.success('Đã cập nhật thuế VAT thành công!');
            } else {
                await vatService.createVATTax(formattedValues as VATTaxCreateInput);
                message.success('Đã tạo thuế VAT mới thành công!');
            }

            setModalVisible(false);
            form.resetFields();
            fetchVATTaxes();
        } catch (error) {
            console.error('Error saving VAT tax:', error);
            message.error(editingVATTax ? 'Không thể cập nhật thuế VAT. Vui lòng thử lại!' : 'Không thể tạo thuế VAT mới. Vui lòng thử lại!');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCalculate = (values: any) => {
        const { amount, vatRate } = values;
        const vatAmount = vatService.calculateVAT(amount, vatRate);
        const totalAmount = vatService.calculateTotalWithVAT(amount, vatRate);

        setCalculatorResult({
            originalAmount: amount,
            vatRate,
            vatAmount,
            totalAmount
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND'
        }).format(amount);
    };

    const getFilteredVATTaxes = () => {
        return vatTaxes.filter(vat => {
            const matchesSearch = !searchText || 
                vat.name.toLowerCase().includes(searchText.toLowerCase()) ||
                vat.description?.toLowerCase().includes(searchText.toLowerCase());
            
            const matchesStatus = statusFilter === 'all' || 
                (statusFilter === 'active' && vat.isActive) ||
                (statusFilter === 'inactive' && !vat.isActive) ||
                (statusFilter === 'default' && vat.isDefault);

            const matchesCategory = categoryFilter === 'all' ||
                vat.applicableCategories.includes(categoryFilter);

            return matchesSearch && matchesStatus && matchesCategory;
        });
    };

    const getCategoryLabel = (category: string) => {
        const found = productCategories.find(cat => cat.value === category);
        return found ? found.label : category;
    };

    const getStatistics = () => {
        const total = vatTaxes.length;
        const active = vatTaxes.filter(vat => vat.isActive).length;
        const inactive = total - active;
        const defaultCount = vatTaxes.filter(vat => vat.isDefault).length;

        return { total, active, inactive, default: defaultCount };
    };

    const columns: TableColumnsType<VATTax> = [
        {
            title: 'Tên thuế VAT',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (name: string, record: VATTax) => (
                <div>
                    <strong>{name}</strong>
                    {record.isDefault && (
                        <Badge 
                            count="Mặc định" 
                            style={{ 
                                backgroundColor: '#52c41a', 
                                marginLeft: 8,
                                fontSize: '10px'
                            }} 
                        />
                    )}
                </div>
            ),
        },
        {
            title: 'Tỷ lệ thuế',
            dataIndex: 'rate',
            key: 'rate',
            width: 120,
            render: (rate: number) => (
                <Tag color="blue" icon={<PercentageOutlined />}>
                    {rate}%
                </Tag>
            ),
            sorter: (a, b) => a.rate - b.rate,
        },
        {
            title: 'Loại sản phẩm áp dụng',
            dataIndex: 'applicableCategories',
            key: 'applicableCategories',
            width: 200,
            render: (categories: string[]) => (
                <div>
                    {categories.map(category => (
                        <Tag key={category} color="geekblue" style={{ marginBottom: 4 }}>
                            {getCategoryLabel(category)}
                        </Tag>
                    ))}
                </div>
            ),
        },
        {
            title: 'Thời gian hiệu lực',
            key: 'validity',
            width: 180,
            render: (_, record: VATTax) => (
                <div>
                    {record.effectiveDate && (
                        <div style={{ fontSize: '12px' }}>
                            <strong>Từ:</strong> {dayjs(record.effectiveDate).format('DD/MM/YYYY')}
                        </div>
                    )}
                    {record.expiredDate && (
                        <div style={{ fontSize: '12px' }}>
                            <strong>Đến:</strong> {dayjs(record.expiredDate).format('DD/MM/YYYY')}
                        </div>
                    )}
                    {!record.effectiveDate && !record.expiredDate && (
                        <Tag color="green">Không giới hạn</Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 120,
            render: (isActive: boolean, record: VATTax) => (
                <Switch
                    checked={isActive}
                    loading={toggleLoading === record.id}
                    onChange={(checked) => handleToggleStatus(record.id!, checked)}
                    checkedChildren="Hoạt động"
                    unCheckedChildren="Tạm dừng"
                    size="small"
                />
            ),
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 200,
            render: (_, record: VATTax) => (
                <Space>
                    <Tooltip title="Xem chi tiết">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => handleView(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Chỉnh sửa">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title={record.isDefault ? "Đã là mặc định" : "Đặt làm mặc định"}>
                        <Button
                            type="text"
                            icon={record.isDefault ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                            onClick={() => !record.isDefault && handleSetDefault(record.id!)}
                            loading={defaultLoading === record.id}
                            disabled={record.isDefault}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa thuế VAT này?"
                        description="Hành động này không thể hoàn tác."
                        onConfirm={() => handleDelete(record.id!)}
                        okText="Xóa"
                        cancelText="Hủy"
                    >
                        <Tooltip title="Xóa">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                loading={deleteLoading === record.id}
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const stats = getStatistics();

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý thuế VAT</h1>
                    <Space>
                        <Button
                            type="primary"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => setIsTourOpen(true)}
                        >
                            Hướng dẫn
                        </Button>
                        <Button
                            ref={calculatorButtonRef}
                            icon={<CalculatorOutlined />}
                            onClick={() => setCalculatorModalVisible(true)}
                        >
                            Máy tính VAT
                        </Button>
                        <Button
                            ref={addButtonRef}
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            Thêm thuế VAT
                        </Button>
                    </Space>
                </div>
            </div>

            {/* Statistics Cards */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Tổng số thuế VAT"
                            value={stats.total}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Đang hoạt động"
                            value={stats.active}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Tạm dừng"
                            value={stats.inactive}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Thuế mặc định"
                            value={stats.default}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Input.Search
                            placeholder="Tìm kiếm theo tên hoặc mô tả..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col span={8}>
                        <Select
                            placeholder="Lọc theo trạng thái"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">Tất cả trạng thái</Option>
                            <Option value="active">Đang hoạt động</Option>
                            <Option value="inactive">Tạm dừng</Option>
                            <Option value="default">Thuế mặc định</Option>
                        </Select>
                    </Col>
                    <Col span={8}>
                        <Select
                            placeholder="Lọc theo loại sản phẩm"
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">Tất cả loại sản phẩm</Option>
                            {productCategories.map(category => (
                                <Option key={category.value} value={category.value}>
                                    {category.label}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Main Table */}
            <Card>
                <div ref={tableRef}>
                    <Table
                        columns={columns}
                        dataSource={getFilteredVATTaxes()}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `Tổng số ${total} thuế VAT`,
                        }}
                        scroll={{ x: 1200 }}
                    />
                </div>
            </Card>

            {/* Add/Edit Modal */}
            <Modal
                title={editingVATTax ? 'Chỉnh sửa thuế VAT' : 'Thêm thuế VAT mới'}
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Tên thuế VAT"
                                rules={[{ required: true, message: 'Vui lòng nhập tên thuế VAT' }]}
                            >
                                <Input placeholder="VD: Thuế VAT đồ uống" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="rate"
                                label="Tỷ lệ thuế (%)"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập tỷ lệ thuế' },
                                    { type: 'number', min: 0, max: 100, message: 'Tỷ lệ thuế phải từ 0-100%' }
                                ]}
                            >
                                <InputNumber
                                    placeholder="VD: 8"
                                    min={0}
                                    max={100}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    addonAfter="%"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label="Mô tả"
                    >
                        <TextArea
                            placeholder="Mô tả chi tiết về thuế VAT này..."
                            autoSize={{ minRows: 2, maxRows: 4 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="applicableCategories"
                        label="Loại sản phẩm áp dụng"
                        rules={[{ required: true, message: 'Vui lòng chọn ít nhất một loại sản phẩm' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Chọn loại sản phẩm áp dụng thuế này"
                            allowClear
                        >
                            {productCategories.map(category => (
                                <Option key={category.value} value={category.value}>
                                    {category.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="effectiveDate"
                                label="Ngày hiệu lực"
                            >
                                <DatePicker
                                    placeholder="Chọn ngày hiệu lực"
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="expiredDate"
                                label="Ngày hết hiệu lực"
                            >
                                <DatePicker
                                    placeholder="Chọn ngày hết hiệu lực"
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="isActive"
                                label="Trạng thái"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="Hoạt động" unCheckedChildren="Tạm dừng" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="isDefault"
                                label="Thuế mặc định"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="Mặc định" unCheckedChildren="Không" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Alert
                        message="Lưu ý"
                        description="Chỉ có thể có một thuế VAT mặc định. Khi đặt thuế này làm mặc định, các thuế mặc định khác sẽ bị hủy."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitLoading}
                            >
                                {editingVATTax ? 'Cập nhật' : 'Thêm mới'}
                            </Button>
                            <Button
                                onClick={() => setModalVisible(false)}
                                disabled={submitLoading}
                            >
                                Hủy
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Detail Modal */}
            <Modal
                title="Chi tiết thuế VAT"
                visible={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        Đóng
                    </Button>
                ]}
                width={600}
            >
                {viewingVATTax && (
                    <Descriptions column={1} bordered>
                        <Descriptions.Item label="Tên thuế VAT">
                            <strong>{viewingVATTax.name}</strong>
                            {viewingVATTax.isDefault && (
                                <Badge 
                                    count="Mặc định" 
                                    style={{ 
                                        backgroundColor: '#52c41a', 
                                        marginLeft: 8 
                                    }} 
                                />
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Tỷ lệ thuế">
                            <Tag color="blue" icon={<PercentageOutlined />}>
                                {viewingVATTax.rate}%
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Mô tả">
                            {viewingVATTax.description || 'Không có mô tả'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Loại sản phẩm áp dụng">
                            <div>
                                {viewingVATTax.applicableCategories.map(category => (
                                    <Tag key={category} color="geekblue" style={{ marginBottom: 4 }}>
                                        {getCategoryLabel(category)}
                                    </Tag>
                                ))}
                            </div>
                        </Descriptions.Item>
                        <Descriptions.Item label="Trạng thái">
                            <Tag color={viewingVATTax.isActive ? 'green' : 'red'}>
                                {viewingVATTax.isActive ? 'Hoạt động' : 'Tạm dừng'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Thời gian hiệu lực">
                            <div>
                                {viewingVATTax.effectiveDate && (
                                    <div>
                                        <strong>Từ:</strong> {dayjs(viewingVATTax.effectiveDate).format('DD/MM/YYYY HH:mm')}
                                    </div>
                                )}
                                {viewingVATTax.expiredDate && (
                                    <div>
                                        <strong>Đến:</strong> {dayjs(viewingVATTax.expiredDate).format('DD/MM/YYYY HH:mm')}
                                    </div>
                                )}
                                {!viewingVATTax.effectiveDate && !viewingVATTax.expiredDate && (
                                    <Tag color="green">Không giới hạn thời gian</Tag>
                                )}
                            </div>
                        </Descriptions.Item>
                        {viewingVATTax.createdAt && (
                            <Descriptions.Item label="Ngày tạo">
                                {dayjs(viewingVATTax.createdAt).format('DD/MM/YYYY HH:mm')}
                            </Descriptions.Item>
                        )}
                        {viewingVATTax.updatedAt && (
                            <Descriptions.Item label="Cập nhật lần cuối">
                                {dayjs(viewingVATTax.updatedAt).format('DD/MM/YYYY HH:mm')}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            {/* Calculator Modal */}
            <Modal
                title="Máy tính thuế VAT"
                visible={calculatorModalVisible}
                onCancel={() => {
                    setCalculatorModalVisible(false);
                    calculatorForm.resetFields();
                    setCalculatorResult(null);
                }}
                footer={null}
                width={500}
            >
                <Form
                    form={calculatorForm}
                    layout="vertical"
                    onFinish={handleCalculate}
                >
                    <Form.Item
                        name="amount"
                        label="Số tiền gốc (VND)"
                        rules={[
                            { required: true, message: 'Vui lòng nhập số tiền' },
                            { type: 'number', min: 0, message: 'Số tiền phải lớn hơn 0' }
                        ]}
                    >
                        <InputNumber
                            placeholder="Nhập số tiền gốc"
                            style={{ width: '100%' }}
                            min={0}
                        />
                    </Form.Item>

                    <Form.Item
                        name="vatRate"
                        label="Tỷ lệ thuế VAT (%)"
                        rules={[
                            { required: true, message: 'Vui lòng chọn tỷ lệ thuế' },
                        ]}
                    >
                        <Select
                            placeholder="Chọn tỷ lệ thuế VAT"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {vatTaxes
                                .filter(vat => vat.isActive)
                                .map(vat => (
                                    <Option key={vat.id} value={vat.rate}>
                                        {vat.name} ({vat.rate}%)
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<CalculatorOutlined />}
                            block
                        >
                            Tính toán
                        </Button>
                    </Form.Item>
                </Form>

                {calculatorResult && (
                    <Card title="Kết quả tính toán" style={{ marginTop: 16 }}>
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Số tiền gốc">
                                <strong>{formatCurrency(calculatorResult.originalAmount)}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Tỷ lệ thuế VAT">
                                <Tag color="blue">{calculatorResult.vatRate}%</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Số tiền thuế VAT">
                                <strong style={{ color: '#fa8c16' }}>
                                    {formatCurrency(calculatorResult.vatAmount)}
                                </strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Tổng tiền (bao gồm VAT)">
                                <strong style={{ color: '#52c41a', fontSize: '16px' }}>
                                    {formatCurrency(calculatorResult.totalAmount)}
                                </strong>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                )}
            </Modal>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>
        </AdminLayout>
    );
};

export default VATManagement; 